import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { Target, Zap, Globe, Users, ShieldCheck, Heart, Layout, BarChart, MessageCircle, Lightbulb, Vote } from 'lucide-react';

const About = () => {
  return (
    <StaticContentPage title="About Us">
      <p className="text-xl font-medium text-gray-700 leading-relaxed mb-8">
        Welcome to Chooziq.
      </p>
      
      <p className="text-lg text-gray-600 mb-12">
        Chooziq is a realtime interaction and collaborative engagement platform built to transform how communities, events, organizations, and learning environments connect and participate online.
        We believe modern digital experiences should be more than passive communication. They should be interactive, collaborative, engaging, and persistent — allowing conversations and ideas to continue long after a live session ends.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        <div className="bg-brand-indigo/5 p-8 rounded-3xl border border-brand-indigo/10">
          <div className="w-12 h-12 rounded-2xl bg-brand-indigo text-white flex items-center justify-center mb-6 shadow-lg shadow-brand-indigo/20">
            <Target size={24} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
          <p className="text-gray-600 leading-relaxed">
            Our mission is to create a unified platform where people can interact in realtime, collaborate meaningfully, participate actively, and build lasting communities around shared experiences. Chooziq is designed to bring together live engagement, collaboration, and community participation into a single ecosystem.
          </p>
        </div>
        <div className="bg-brand-cyan/5 p-8 rounded-3xl border border-brand-cyan/10">
          <div className="w-12 h-12 rounded-2xl bg-brand-cyan text-white flex items-center justify-center mb-6 shadow-lg shadow-brand-cyan/20">
            <Zap size={24} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
          <p className="text-gray-600 leading-relaxed">
            We envision a future where online communities and live experiences are more interactive, collaborative, engaging, and connected. Our goal is to provide infrastructure for communities and organizations to create dynamic digital experiences that encourage participation, collective learning, and ongoing collaboration.
          </p>
        </div>
      </div>

      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">What We Do</h2>
        <p className="text-gray-600 mb-8">Chooziq enables users and organizations to:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {[
            { icon: Globe, text: "Host live sessions and events" },
            { icon: Users, text: "Create and manage communities" },
            { icon: MessageCircle, text: "Run realtime Q&A sessions" },
            { icon: Lightbulb, text: "Collaborate through brainstorming boards" },
            { icon: Vote, text: "Conduct live polls and audience voting" },
            { icon: Layout, text: "Share resources and discussions" },
            { icon: BarChart, text: "Preserve engagement beyond the live experience" }
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-brand-indigo shadow-sm border border-gray-100">
                <item.icon size={20} />
              </div>
              <span className="font-medium text-gray-700">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">Why Chooziq Exists</h2>
        <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
          <p className="text-gray-600 leading-relaxed mb-6">
            Many digital platforms focus on only one part of online interaction. Some are built for messaging, discussions, video meetings, collaboration, or event hosting. However, meaningful engagement often becomes fragmented across multiple disconnected tools.
          </p>
          <p className="text-gray-600 leading-relaxed font-medium">
            Chooziq was created to unify these experiences into a single platform centered around participation, interaction, collaboration, and realtime engagement. We believe live interactions should not disappear when a session ends.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 tracking-tight">Our Core Principles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Principles */}
          {[
            { title: "Realtime Engagement", desc: "Interactions should feel immediate, responsive, and alive.", icon: Zap },
            { title: "Collaboration", desc: "Communities grow through participation, contribution, and shared ideas.", icon: Users },
            { title: "Persistence", desc: "Important discussions and collaborative insights should remain valuable after sessions.", icon: BarChart },
            { title: "Community", desc: "Strong communities are built through continuous interaction and meaningful participation.", icon: Heart },
            { title: "Accessibility", desc: "Interactive experiences should be available to organizations and teams of all sizes.", icon: ShieldCheck }
          ].map((principle, idx) => (
            <div key={idx} className="p-6 rounded-3xl border border-gray-100 bg-white shadow-sm">
              <principle.icon className="text-brand-indigo mb-4" size={24} />
              <h4 className="font-bold text-gray-900 mb-2">{principle.title}</h4>
              <p className="text-sm text-gray-500 leading-relaxed">{principle.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">Built for Modern Communities</h2>
        <p className="text-gray-600 mb-8">
          Whether hosting a classroom discussion, brainstorming workshop, live event, or collaborative community session, Chooziq provides tools for meaningful engagement and participation for:
        </p>
        <div className="flex flex-wrap gap-3">
          {[
            'Universities & Schools', 'Conferences & Events', 'Tech & Innovation Communities', 
            'Organizations & Teams', 'Workshops & Webinars', 'Hackathons', 
            'Collaborative Learning Groups', 'Professional Networks'
          ].map(tag => (
            <span key={tag} className="px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold text-gray-600">{tag}</span>
          ))}
        </div>
      </section>

      <div className="p-10 bg-gray-900 rounded-3xl text-white text-center">
        <h3 className="text-3xl font-bold mb-4">Contact Us</h3>
        <p className="text-gray-400 mb-8 max-w-lg mx-auto">
          We would love to hear from you. Whether you have questions or want to learn more about how Chooziq can help your community.
        </p>
        <div className="flex flex-col items-center gap-4">
          <a href="mailto:contact@chooziq.com" className="text-xl font-bold text-brand-indigo hover:text-brand-indigo/80 transition-colors">
            contact@chooziq.com
          </a>
          <div className="flex gap-6 mt-4">
            {['Twitter', 'LinkedIn', 'Discord'].map(social => (
              <span key={social} className="text-sm font-medium text-gray-500 cursor-pointer hover:text-white transition-colors">{social}</span>
            ))}
          </div>
        </div>
      </div>
    </StaticContentPage>
  );
};

export default About;
